import { FixedSizeList as List } from "react-window";
import { Dropdown } from "antd";
import { useState, useCallback } from "react";
import classNames from "classnames";
import styled from "styled-components";
import { faTimes } from "@fortawesome/pro-light-svg-icons/faTimes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { TypeAheadInput } from "common/components/filterBar/TypeAhead";
import { getters } from "common/components/getters/getters";
import { useDocumentClickOutsideTarget } from "common/hooks/index";

import { FilterItemRow } from "./FilterItemRowStyle";
import { renderTokenIndicator } from "./FilterBarUtils";

export function FilterBarCategory(props) {
    const selectedVals = props.qualifiers.value.find((v) => v.category === props.category)?.values.map((v) => v.value);
    const filtered = selectedVals ? props.data.filter((v) => !selectedVals.includes(v.ref)) : props.data;
    const [filteredData, setFilteredData] = useState([...filtered]);
    const [dropDownOpen, setDropDownOpen] = useState(false);
    
    const handleClickOutsideDropDown = useCallback(() => setDropDownOpen(false), []);
    
    useDocumentClickOutsideTarget(handleClickOutsideDropDown, `.category_dd${props.category}`);
    
    const itemClick = (id) => () => {
        const operators = props.selectedCategories.find((s) => s.category === props.category)?.operators || [];
        const op = operators.length === 2 || operators[0] === "=" ? "" : "-";
        props.filterItemClick(op, id, false);
    };
    
    const searchResults = (results) => {
        if (results.value.length) {
            setFilteredData(results.raw);
            setDropDownOpen(true);
        } else {
            setFilteredData([...filtered]);
            setDropDownOpen(false);
        }
    };
    
    const clickDropDown = () => {
        setDropDownOpen(true);
    };
    
    const getTypeAhead = (category, typeAheadData) => (
        // TODO: deprecate overlay, refactor menu
        <Dropdown className={`category_dd${props.category}`} overlay={renderPivotList(filteredData)} open={dropDownOpen} onClick={clickDropDown} placement="bottomLeft" getPopupContainer={(trigger) => trigger}>
            <TypeAheadMenu><TypeAheadInput originalData={typeAheadData} srcData={typeAheadData} onTypeAhead={searchResults} showSearchIcon={false}/></TypeAheadMenu>
        </Dropdown>
    );
    
    const renderPivotList = (listData) => {
        const listResults = (filterList, selected) => function({
            key, index, isScrolling, isVisible, style
        }) {
            const item = filterList[index];
            return (
                <FilterItemRow key={`${key}_${item.id}`} style={style} className="typeahead-row" onClick={itemClick(item.id)}>
                    <div className="row-label">
                        <div className="item-value">{item.alias}</div>
                    </div>
                </FilterItemRow>
            );
        };
        
        if (listData.length) {
            return (
                <div className="items-list">
                    <List
                        style={{ paddingBottom: "15px" }}
                        height={190}
                        itemCount={listData.length}
                        itemSize={26}
                        width={200}
                    >
                        {listResults(listData)}
                    </List>
                </div>
            );
        }
        return (
            <div className="items-list empty">
                <div>No results...</div>
            </div>
        );
    };
    
    const getTokensInCategory = (category, numOperators) => {
        if (numOperators === 2) {
            return props.getTokens(props.category, true).filter((t) => t.props.token.operator === "=");
        }
        return props.getTokens(props.category, true);
    };
        
    const alias = getters.findPivotName(props.category);
    const categoryOperators = props.selectedCategories.find((s) => s.category === props.category)?.operators || [];
    const allTokens = getTokensInCategory(props.category, categoryOperators.length);
    const include = (categoryOperators.length === 2) || (categoryOperators.length === 1 && categoryOperators[0] === "=");
    const exclude = categoryOperators.length === 1 && categoryOperators[0] === "!=";
    
    const toggleToOperater = (operator = "") => () => {
        const selectedItems = props.getTokens(props.category, true).map((t) => t.props.token.id);
        props.onUpdate({
            operator,
            selectedItems,
            category: props.category
        });
    };
        
    return (
        <CategoryRow>
            <div className={classNames("operators", { include }, { exclude })}>
                <div title="Change to exclude" onClick={toggleToOperater("-")}>{renderTokenIndicator(["!="])}</div>
                <div title="Change to include" onClick={toggleToOperater("")}>{renderTokenIndicator(["="])}</div>
            </div>
            <div className="category">{alias}</div>
            <div className="tokens">
                {allTokens}
                {!props.readOnly && getTypeAhead(props.category, [...filtered])}
            </div>
            <div className="clear-category" onClick={() => { props.clearCategory(props.category); }}>
                <FontAwesomeIcon icon={faTimes} />
            </div>
        </CategoryRow>
    );
}

const TypeAheadMenu = styled.div`
    width: 210px;
    display: inline-block;
    .items-list {
        outline: none;
        width: 204px;
        min-height: 35px;
        padding: 5px 3px;
        opacity: 1.0;
        background: ${(props) => props.theme.colors["canvas-white"]};
        z-index: 999;
        border: 1px solid ${(props) => props.theme.colors["gray-soft"]};
    }
    .items-list.empty {
        min-height: 60px;
    }
`;

const CategoryRow = styled.div`
    display: flex;
    border-bottom: 1px solid ${(props) => props.theme.colors["canvas-dark"]};
    padding: 6px 2px 2px 2px;
    min-height: 32px;
    
    .operators {
        flex: 0;
        white-space: nowrap;
        display: flex;
        font-family: ${(props) => props.theme.text.light};
        font-size: ${(props) => props.theme.fontSizeLarge};
        cursor: pointer;
        .include {
            margin: 5px 12px 5px 0;
            color: ${(props) => props.theme.colors["green-leaf"]};
        }
        .exclude {
            margin: 7px 12px 5px 0;
            color: ${(props) => props.theme.colors.jason};
        }
    }
   
    .operators.include {
        .include {
            display: block;
        }
        .exclude {
            display: none;
        }
    }
    .operators.include:hover {
        .include {
            display: none;
        }
        .exclude {
            display: block;
        }
    }
    .operators.exclude {
        .include {
            display: none;
        }
        .exclude {
            display: block;
        }
    }
    .operators.exclude:hover {
        .include {
            display: block;
        }
        .exclude {
            display: none;
        }
    }
    
    .category {
        flex: 0;
        width: 150px;
        min-width: 150px;
        white-space: nowrap;
        height: 32px;
        padding-top: 5px;
    }

    .tokens {
        flex: 1;

        .token {
            .operator {
                display: none;
            }
        }

        .add-filter {
            cursor: pointer;
            padding: 6px 10px;
            display: inline-block;
            color: ${(props) => props.theme.colors["gray-mid"]};
            &:hover {
                text-decoration: underline;
            }
        }
    }

    .type-ahead-container {
        display: inline-block;
        position: relative;
    }
    
    .clear-category {
        cursor: pointer;
        padding: 8px;
    }
`;
